"use client";

import { cn } from "@workspace/ui/lib/utils";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";

type DashboardContentShellProps = {
  children: ReactNode;
  orgSlug: string;
};

export function DashboardContentShell({
  children,
  orgSlug,
}: DashboardContentShellProps) {
  const pathname = usePathname();
  const configurationPathname = pathname.replace(/\/+$/, "");
  const configBasePath = `/${orgSlug}/configuracion`;
  const planSelectionPath = `${configBasePath}/plan/seleccionar`;
  const incomeStatementPath = `/${orgSlug}/contabilidad`;
  // Financial statements scroll inside the report, so the table header stays
  // visible and the horizontal scrollbar stays on screen.
  const statementPaths = new Set([
    incomeStatementPath,
    `${incomeStatementPath}/balance-general`,
    `${incomeStatementPath}/flujo-efectivo`,
  ]);
  const isConfigurationRoute =
    configurationPathname === configBasePath ||
    configurationPathname.startsWith(`${configBasePath}/`);

  if (isConfigurationRoute) {
    let maxWidth = "max-w-3xl";
    if (configurationPathname === planSelectionPath) {
      maxWidth = "max-w-7xl";
    } else if (configurationPathname.endsWith("/stock-seguridad")) {
      maxWidth = "max-w-4xl";
    }

    return (
      <div className="p-6 has-[[data-slot=page-shell]]:p-0">
        <div className={cn("mx-auto flex w-full flex-col gap-4", maxWidth)}>
          {children}
        </div>
      </div>
    );
  }

  if (statementPaths.has(pathname)) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        {children}
      </div>
    );
  }

  if (pathname.startsWith(`/${orgSlug}/conciliacion/`)) {
    // The workbench sizes itself against this box instead of against the
    // viewport, so a support or trial banner above it cannot push its sticky
    // action bar under the fold.
    return (
      <div className="flex flex-col lg:h-full lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        {children}
      </div>
    );
  }

  return <div className="flex flex-col gap-4">{children}</div>;
}
